import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { NoteHandler, NOTE_MAX_LENGTH } from "../../Apihander/Note/Note";
import { errorToast, successToast } from "../../utils/notificationUtils";
import { NoteEditorModal } from "./NotesModals";
import {
    ConfirmDialog, EMPTY_NOTE_TEXT, Ic, PageHeader, Spinner,
    fmt, friendlyDate, newClientId, noteSeconds, readErrorMessage, recordingName, recordingSeconds, toNote,
} from "./notesShared";

// Player and note box beside the notes list; a note is pinned to the time shown when typing starts.
const AddNotes = ({ audio, startAt, onBack, onNotesChange }) => {
    const audioRef = useRef(null);
    const inputRef = useRef(null);
    const [pos, setPos] = useState(startAt || 0);
    const [duration, setDuration] = useState(recordingSeconds(audio));
    const [playing, setPlaying] = useState(false);
    const [pendingTs, setPendingTs] = useState(null);
    const [text, setText] = useState("");
    const [adding, setAdding] = useState(false);
    const [editingNote, setEditingNote] = useState(null);
    const [savingEdit, setSavingEdit] = useState(false);
    const [deletingNote, setDeletingNote] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const notes = audio?.notes || [];
    const total = duration || 1;

    useEffect(() => () => audioRef.current?.pause(), []);

    const handleLoadedMetadata = () => {
        const element = audioRef.current;
        if (Number.isFinite(element.duration) && element.duration > 0) {
            setDuration(element.duration);
        }
        if (startAt) element.currentTime = startAt;
    };

    const seekToSeconds = (seconds) => {
        const next = Math.min(total, Math.max(0, seconds));
        if (audioRef.current) audioRef.current.currentTime = next;
        setPos(next);
    };

    const togglePlay = () => {
        const element = audioRef.current;
        if (!element) return;
        if (element.paused) {
            Promise.resolve(element.play()).catch(() => errorToast("This recording could not be played"));
        } else {
            element.pause();
        }
    };

    const handleSeekClick = (e) => {
        const bar = e.currentTarget.getBoundingClientRect();
        seekToSeconds(Math.min(1, Math.max(0, (e.clientX - bar.left) / bar.width)) * total);
    };

    const jumpTo = (seconds) => {
        seekToSeconds(seconds);
        successToast(`Jumped to ${fmt(seconds)}`);
    };

    const freezeTs = () => {
        audioRef.current?.pause();
        setPendingTs(audioRef.current ? audioRef.current.currentTime : pos);
    };

    const commitAddNote = async () => {
        // A blank note is allowed; it just marks the moment.
        const content = text.trim();
        if (adding) return;

        const ts = pendingTs === null ? pos : pendingTs;
        setAdding(true);
        try {
            const response = await NoteHandler.doCreateNote({
                audioId: audio._id,
                clientId: newClientId(),
                content,
                timestampMs: Math.round(ts * 1000),
            });
            if (response?.statusCode === 200) {
                onNotesChange(audio._id, (prev) => [...prev, toNote(response.data)]);
                setText("");
                setPendingTs(null);
                successToast("Note added");
            } else {
                errorToast(readErrorMessage(response));
            }
        } finally {
            setAdding(false);
        }
    };

    const saveEdit = async (content) => {
        setSavingEdit(true);
        try {
            const response = await NoteHandler.doUpdateNote(editingNote._id, { content });
            if (response?.statusCode === 200) {
                onNotesChange(audio._id, (prev) => prev.map((note) => (note._id === editingNote._id ? toNote(response.data) : note)));
                setEditingNote(null);
                successToast("Note updated");
            } else {
                errorToast(readErrorMessage(response));
            }
        } finally {
            setSavingEdit(false);
        }
    };

    const confirmDelete = async () => {
        setDeleting(true);
        try {
            const response = await NoteHandler.doDeleteNote(deletingNote._id);
            if (response?.statusCode === 200) {
                onNotesChange(audio._id, (prev) => prev.filter((note) => note._id !== deletingNote._id));
                setDeletingNote(null);
                successToast("Note deleted");
            } else {
                errorToast(readErrorMessage(response));
            }
        } finally {
            setDeleting(false);
        }
    };

    const pct = (pos / total) * 100;

    return (
        <>
            <PageHeader title={recordingName(audio)} onBack={onBack} icon={<Icon icon="lucide:notebook-pen" />} tone="amber" />
            <div className="nt-content">
                <audio
                    ref={audioRef}
                    src={audio?.audio?.url}
                    preload="metadata"
                    onLoadedMetadata={handleLoadedMetadata}
                    onTimeUpdate={(e) => setPos(e.target.currentTime)}
                    onPlay={() => setPlaying(true)}
                    onPause={() => setPlaying(false)}
                    onEnded={() => setPlaying(false)}
                />
                <div className="nt-split">
                    <div className="nt-split-main">
                        <div className="nt-card nt-playercard">
                            <div className="nt-player-head">
                                <span className="nt-av rec"><Ic name="mic" /></span>
                                <div className="nt-grow">
                                    <p className="nt-title">{recordingName(audio)}</p>
                                    <span className="nt-sub">{friendlyDate(audio?.createdAt)}</span>
                                </div>
                                <span className="nt-clock">
                                    {fmt(pos)} / {fmt(duration)}
                                </span>
                            </div>
                            <div className="nt-seek" onClick={handleSeekClick}>
                                <i style={{ width: `${pct}%` }} />
                                {notes.map((note) => (
                                    <span key={note._id} className="nt-mk" style={{ left: `${Math.min(100, (noteSeconds(note) / total) * 100)}%` }} />
                                ))}
                                <b style={{ left: `${pct}%` }} />
                            </div>
                            <div className="nt-times"><span>{fmt(pos)}</span><span>{fmt(duration)}</span></div>
                            <div className="nt-pcontrols">
                                <button type="button" className="nt-pbtn" aria-label="Back 15 seconds" onClick={() => seekToSeconds(pos - 15)}><Ic name="back15" /></button>
                                <button type="button" className="nt-pbtn main" onClick={togglePlay}><Ic name={playing ? "pause" : "play"} size="l" /></button>
                                <button type="button" className="nt-pbtn" aria-label="Forward 15 seconds" onClick={() => seekToSeconds(pos + 15)}><Ic name="fwd15" /></button>
                            </div>
                        </div>

                        <div className="nt-card nt-composer">
                            <div className="nt-composer-head">
                                <span className="nt-composer-title">
                                    Add a note at <span className="nt-ts">{fmt(pendingTs === null ? pos : pendingTs)}</span>
                                </span>
                                <span className="nt-count">{`${text.length}/${NOTE_MAX_LENGTH}`}</span>
                            </div>
                            <div className="nt-noteinput">
                                <input
                                    ref={inputRef}
                                    className="form-control input-field nt-field"
                                    placeholder="Type your note"
                                    value={text}
                                    maxLength={NOTE_MAX_LENGTH}
                                    disabled={adding}
                                    onFocus={freezeTs}
                                    onChange={(e) => setText(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") commitAddNote(); }}
                                />
                                <button type="button" className="nt-btn" disabled={adding} onClick={commitAddNote}>
                                    {adding ? <Spinner /> : "Add"}
                                </button>
                            </div>
                            <p className="nt-help">
                                The note is saved at the time shown above. Play the recording, then start typing to pin a moment.
                            </p>
                        </div>
                    </div>

                    <div className="nt-split-side">
                        <div className="nt-sectitle">
                            {notes.length ? `Notes on this recording · ${notes.length}` : "Notes on this recording"}
                        </div>
                        {notes.length ? notes.map((note) => (
                            <div className="nt-note" key={note._id}>
                                <button type="button" className="nt-ts" title="Play from here" onClick={() => jumpTo(noteSeconds(note))}>
                                    {fmt(noteSeconds(note))}
                                </button>
                                <span className="nt-txt">{note.content || EMPTY_NOTE_TEXT}</span>
                                <span className="nt-acts">
                                    <button type="button" className="nt-iconbtn" aria-label="Edit" onClick={() => setEditingNote(note)}><Ic name="edit" size="s" /></button>
                                    <button type="button" className="nt-iconbtn danger" aria-label="Delete" onClick={() => setDeletingNote(note)}><Ic name="trash" size="s" /></button>
                                </span>
                            </div>
                        )) : (
                            <p className="nt-none">No notes yet. Add one on the left.</p>
                        )}
                    </div>
                </div>
            </div>

            {editingNote && (
                <NoteEditorModal
                    timestampSec={noteSeconds(editingNote)}
                    initialValue={editingNote.content}
                    saving={savingEdit}
                    onClose={() => setEditingNote(null)}
                    onSave={saveEdit}
                />
            )}
            {deletingNote && (
                <ConfirmDialog
                    title="Delete note?"
                    message="This note will be permanently deleted."
                    busy={deleting}
                    onCancel={() => setDeletingNote(null)}
                    onConfirm={confirmDelete}
                />
            )}
        </>
    );
};

export default AddNotes;
